import {
  CellTemplate,
  Compatible,
  isAlphaNumericKey,
  isNavigationKey,
  keyCodes,
  Uncertain,
  UncertainCompatible,
} from "@silevis/reactgrid";
import { KeyboardEvent, ReactNode } from "react";
import { DATE_TIME_FORMAT } from "@lhd/constants";
import { Styles } from "./Style";
import moment, { Moment } from "moment";
import { CellCustomType } from "../../type";
import CommonDatePicker from "@lhd/components/CommonDatePicker";

const toSheetDateTime = (raw: string | Moment | null | undefined) => {
  if (raw == null || raw === "") return null;
  if (moment.isMoment(raw)) {
    return raw.isValid() ? raw.format(DATE_TIME_FORMAT.DATE_TIME) : null;
  }
  const parsed = moment(
    String(raw).trim(),
    [
      DATE_TIME_FORMAT.DATE_TIME,
      DATE_TIME_FORMAT.DATE_TIME_DASH_SEC,
      DATE_TIME_FORMAT.DATE_JP_TIME,
      DATE_TIME_FORMAT.DATE_TIME_JP,
      DATE_TIME_FORMAT.DATE_DASH,
      DATE_TIME_FORMAT.DATE_DEFAULT,
      DATE_TIME_FORMAT.DATE_JP,
    ],
    true,
  );
  if (!parsed.isValid()) {
    const fallback = moment(String(raw));
    return fallback.isValid()
      ? fallback.format(DATE_TIME_FORMAT.DATE_TIME)
      : null;
  }
  return parsed.format(DATE_TIME_FORMAT.DATE_TIME);
};

export class DateTimeCellTemplate implements CellTemplate<CellCustomType> {
  getCompatibleCell(
    uncertainCell: Uncertain<CellCustomType>,
  ): Compatible<CellCustomType> {
    const uncertainCellAs = uncertainCell as CellCustomType;

    return {
      value: NaN,
      ...uncertainCellAs,
    };
  }

  update(
    cell: Compatible<CellCustomType>,
    cellToMerge: UncertainCompatible<CellCustomType>,
  ): Compatible<CellCustomType> {
    const text = (cellToMerge.text ?? "").replace(/\r/g, "").replace(/\n/g, "");
    const saved = toSheetDateTime(text) || text;
    return this.getCompatibleCell({ ...cell, text: saved });
  }

  getClassName(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
  ): string {
    return cell.className || "";
  }

  handleKeyDown(
    cell: Compatible<CellCustomType>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<CellCustomType>; enableEditMode: boolean } {
    if (!ctrl && !alt && isAlphaNumericKey(keyCode))
      return { cell, enableEditMode: true };
    return {
      cell,
      enableEditMode:
        keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
    };
  }
  render(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void,
  ): ReactNode {
    if (!isInEditMode) return cell.displayText;

    const commitRaw = (raw: string | Moment | null) => {
      if (raw == null || (typeof raw === "string" && !String(raw).trim())) {
        onCellChanged(this.getCompatibleCell({ ...cell, text: "" }), true);
        return;
      }
      const saved = toSheetDateTime(raw);
      if (!saved) return;
      onCellChanged(this.getCompatibleCell({ ...cell, text: saved }), true);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.keyCode === keyCodes.ENTER) {
        e.preventDefault();
        e.stopPropagation();
        const input = e.currentTarget.querySelector("input");
        commitRaw(input?.value ?? "");
        return;
      }
      if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode)) {
        e.stopPropagation();
      }
    };

    return (
      <Styles
        onClick={(e) => {
          e.preventDefault();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <CommonDatePicker
          autoFocus
          open
          showTime={{ format: "HH:mm" }}
          displayFormat={DATE_TIME_FORMAT.DATE_JP_TIME}
          placeholder=""
          value={
            cell.text && moment(cell.text).isValid() ? moment(cell.text) : null
          }
          style={{ width: "100%", height: "100%" }}
          wrapperStyle={{ width: "100%", height: "100%" }}
          onChange={(_saved, date) => commitRaw(date)}
          onKeyDown={handleKeyDown}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </Styles>
    );
  }
}
